import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { colors, css, frames, useMediaQuery } from '../../../../Styles';
import { Button, PageHeader, FeedCurveCard, ButtonList, NewInputHandles, NewInput, FeedCurveViewer, NewModal, MainContentPaddingTypes, Overlay } from '../../../../Components';
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/Routers/root"
import { useAppSelector } from '../../../../Store';
import { Graph_QL } from '../../../../Services/Agrisys_GqlDataCaller';
import { useFeedCurves } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { ButtonTypes, ListStyle_Types } from '../../../../Definitions';
import CardsView from '../../../../Assets/TestData/cardsView';

export default function FeedCurves() {
  const {t} = useTranslation()
  const navigator = useNavigate()
  const [selectedFeedcurve, setSelectedFeedcurve] = useState<number|undefined>(undefined)
  const [feedcurveModal, setFeedcurveModal] = useState<boolean>(false)
  const [copyFeedCurveMode, setCopyFeedCurveMode] = useState<boolean>(false)
  const [addFeedcurveModal, setAddFeedcurveModal] = useState<boolean>(false)
  const [copyFeedcurveModal, setCopyFeedcurveModal] = useState<boolean>(false)
  const hideBtnText = useMediaQuery(`(max-width: 450px)`);
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  // #region | Data
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = Graph_QL.ObjToString({domain: farm ?? ""});
  const connection = Graph_QL.create({domain: farm ?? ""});

  const {call: getFeedCurves, data: feedCurvesState, createFeedCurve} = useFeedCurves(connection);
  const feedCurveNameInputRef = useRef<NewInputHandles>(null);
  
  const feedCurvesData = useMemo(() => {
    return feedCurvesState && feedCurvesState[domain] ? feedCurvesState[domain].data : [];
  },[domain, feedCurvesState])
  useEffect(() => {
    if (!feedCurvesState[domain] || (feedCurvesState[domain].data.length > 0 && !feedCurvesState[domain].data.find((feedCurves) => feedCurves.milestones !== undefined))) {
      getFeedCurves({fetchAmount: 50, ifFeedCurveInfo: true})
    }
  },[domain, getFeedCurves, feedCurvesState])
  
  const feedCurvesIds = useMemo(() => {
    return feedCurvesData.map((feedCurve) => feedCurve.feedCurveId)
  },[feedCurvesData])
  // #endregion

  // Save current FeedCurveId and selected FeedCurveData to const
  const selectedFeedCurveData = feedCurvesData.find(feedCurve => feedCurve.feedCurveId === selectedFeedcurve);
  
  // Find currentIndex to toggle between feedCurves
  const currentIndex = feedCurvesIds.indexOf(selectedFeedcurve!);

  // Select Feed Curve Handler
  const onFeedCurveSelect = (id:number) => {
    mobileXs ? RootNavigation(navigator, Pages.feedCurves, Pages.feedCurve, true, {selected:selectedFeedCurveData?.feedCurveId})
    : setSelectedFeedcurve(id)
    copyFeedCurveMode
    ? setCopyFeedcurveModal(true)
    : setFeedcurveModal(true)
  }

  // Handle modal close
  const handleCloseBtn = () => {
    setAddFeedcurveModal(false) 
    setSelectedFeedcurve(undefined)
    if (copyFeedCurveMode) {
      setCopyFeedcurveModal(false);
    }
  }

  const handleCreateFeedCurve = async () => {
    if (feedCurveNameInputRef.current?.value) {
      handleCloseBtn();
      const response = await createFeedCurve(feedCurveNameInputRef.current.value.toString());
      if (response.success) {
        RootNavigation(navigator, Pages.feedCurves, Pages.feedCurve, true, {selected: response.feedcurve_id})
      }
    }
  }

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <div style={{display: "flex", flexDirection: "row", alignItems: "start", justifyContent: "space-between"}}>
          <PageHeader topHeader={t(`menuHeaders.${Pages.feedCurves}`, {defaultValue: "feed curves"})} subHeader={t("menuHeaders.overview", {defaultValue: "overview"})}/>
          <div style={{display: "block", zIndex: copyFeedCurveMode? "99999" : "auto"}}>
            <ButtonList
              contents={[copyFeedCurveMode ? t("buttons.cancel", {defaultValue: "cancel"}) : t("feedCurvePages.feedCurves.copyFeedCurve", {defaultValue: "copy feed curve"})]} 
              onClick={(i => {
                if (i === 0) {setCopyFeedCurveMode(copyFeedCurveMode ? false : true)}
              })}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none", backgroundColor: copyFeedCurveMode ? colors.greyPrimary : colors.greyDarkPrimary} : {backgroundColor: colors.greyDarkSecondary})},
                hoverStyles: {...(!hideBtns ? { backgroundColor: copyFeedCurveMode ? colors.greySecondary : colors.blackPrimary } : { backgroundColor: colors.blackPrimary })}
              }}
              listOverrides={{
                listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
              }}
              responsiveOverrides={{
                isResponsive: !hideBtns ? false : true,
                popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" },
                openContent: hideBtnText ? "" : "Options"
              }}              
            />
          </div>
        </div>
      </frames._page_inner_header>

      <frames._page_inner_content>
        {/* Wrap of Feedcurve cards with miniature feedCurveViewer */}
        <frames._page_inner_content_scroll>
          {copyFeedCurveMode ? <Overlay overrides={{ zIndex: 9999 }} /> : <></>}
          <div style={{zIndex: copyFeedCurveMode? "99999" : "auto", position: copyFeedCurveMode? "relative" : "unset"}}>
            <CardsView
              data={feedCurvesData}
              card={(item, index) => {
                if ('feedCurveId' in item) {
                  return (
                    <FeedCurveCard
                      key={index}
                      feedCurve={item}
                      onFeedCurveSelect={onFeedCurveSelect}
                    />
                  );
                }
                return <></>;
              }}
              cardBtn={{
                content: t("feedCurvePages.feedCurves.createFeedCurve", { defaultValue: "create feed curve" }),
                subContent: t("feedCurvePages.feedCurves.createFeedCurveFromScratch", { defaultValue: "Create feed curve from scratch" }),
                isCardBtnDisabled: copyFeedCurveMode,
                onCardBtnClick: () => setAddFeedcurveModal(true)
              }}
            />
          </div>
        </frames._page_inner_content_scroll>
      </frames._page_inner_content>
      {/* Popup Modal for clicked feedcurve */}
      <NewModal
        modalState={!!feedcurveModal}
        overrides={{
          backgroundColor: colors.greyLightTertiary
        }}
        predefinedButtons={{
          onClose: () => setFeedcurveModal(false),
          onNext: () => setSelectedFeedcurve(feedCurvesIds[(currentIndex + 1) % feedCurvesIds.length]),
          onPrev: () => setSelectedFeedcurve(feedCurvesIds[(currentIndex - 1 + feedCurvesIds.length) % feedCurvesIds.length])
        }}
        header={`${selectedFeedCurveData?.name}`}
        subHeader={`${t("feed.feedCurve.feedCurve", {defaultValue: "Feed Curve"})} ${selectedFeedcurve}`}
        mainContent={
          <div style={{padding: "0px 20px 20px 20px"}}>
            {/* FeedCurveViewer */}
            {selectedFeedCurveData?.milestones && (
              <FeedCurveViewer data={selectedFeedCurveData} showComponentLabels showMilestoneLabels/>
            )}
            {/* Show more FeedCurve details btn */}
            <div style={{margin: "20px auto 0px auto", width: "100%", textAlign: "center"}}>
              <Button
                content={t("feedCurvePages.feedCurves.feedCurveDetails", {defaultValue: "Feed Curve Details"})}
                onClick={() => {
                  RootNavigation(navigator, Pages.feedCurves, Pages.feedCurve, true, {selected:selectedFeedCurveData?.feedCurveId})
                }}
                buttonType={ButtonTypes.primaryButton}
              />
            </div>
          </div>
        }
      />
      {/* Add / Copy Feed Curve Modal */}
      <NewModal
        modalState={!!addFeedcurveModal||!!copyFeedcurveModal}
        overrides={{
          mainContentPadding: MainContentPaddingTypes.secondary,
        }}
        predefinedButtons={{
          onClose: handleCloseBtn
        }}
        header={t("feedCurvePages.feedCurves.newFeedCurve", {defaultValue: "New Feed Curve"})}
        subHeader={copyFeedcurveModal ? `${t("feedCurvePages.feedCurves.fromFeedCurve", {defaultValue: "From Feed Curve"})} ${selectedFeedcurve}: ${selectedFeedCurveData?.name}` : "" }
        mainContent={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "40px"}}>
            <NewInput ref={feedCurveNameInputRef} header={t("feedCurvePages.feedCurves.feedCurveName", {defaultValue: "Feed Curve Name"})} valueOverrides={{valueType: "string"}}/>
            <Button
                content={t("feedCurvePages.feedCurves.createFeedCurve", {defaultValue: "Create Feed Curve"})}
                onClick={handleCreateFeedCurve}
                buttonType={ButtonTypes.primaryButton}
                overrides={{
                  baseButton: {...css.button_FitContent}
                }}
              />
          </div>
        }
      />
    </frames.Page_inner>
  );
}  